import { UpdatePageLayout } from 'framework/ui/layout';
import { Typography } from '@mui/material';
import { useProjectUrlParams } from './hooks/useProjectUrlParams';
import ProjectUpdateForm from './forms/ProjectUpdateForm';
import { useProjectId } from 'domain/shared/project';
import { UpdateForm } from 'framework/form';
import { useAccountResourceService } from 'domain/shared/account';
import {
  BillingType,
  useNewProjectService,
  useProjectFinanceService,
  useProjectTeamService,
  isDocusignLinkValid,
  useNewProjectUrl,
} from 'domain/shared/newProject';

const UpdateProjectLayout = () => {
  const id = useProjectId();
  const urlParams = useProjectUrlParams();
  const { getById, getProjectUpdateInitValidationSchema, updateInitialProject } = useNewProjectService();
  const { getById: getAccountById } = useAccountResourceService();
  const { getBudget, budgetsFinanceSearch } = useProjectFinanceService();
  const { getProjectTeamByProjectId } = useProjectTeamService();
  const { getSingleEntityUrl } = useNewProjectUrl();

  return (
    <>
      <Typography variant="h1" color="brand" pb={2}>
        Project Update Form
      </Typography>
      <UpdatePageLayout buttons={<></>}>
        <UpdateForm
          entityId={id}
          submitFormFunc={(projectId, data) => updateInitialProject(projectId, data)}
          afterSubmit={{
            redirect: () => getSingleEntityUrl(id),
            message: 'Great! The project has been successfully updated!',
          }}
          getValidationSchemaFunc={getProjectUpdateInitValidationSchema}
          getDataFunc={async projectId => {
            return getById(projectId)
              .then(project => {
                return Promise.all([
                  Promise.resolve(project),
                  project.defaultBudgetId ? getBudget(project.defaultBudgetId) : Promise.resolve(null),
                  project.defaultBudgetId
                    ? budgetsFinanceSearch({
                        page: 0,
                        size: 100,
                        sort: {
                          createdAt: 'ASC',
                        },
                        filter: {
                          id: {
                            in: [`${project.defaultBudgetId}`],
                          },
                        },
                      }).then(response => response.result?.[0]?.structure?.[0])
                    : Promise.resolve(null),
                  getProjectTeamByProjectId(project.id),
                  getAccountById(project.accountId),
                ]);
              })
              .then(([project, budget, financeBudget, projectTeam, account]) => {
                return Promise.resolve({
                  urlParams: urlParams,
                  requestId: urlParams?.requestId,
                  inputData: {
                    opportunityId: urlParams?.opportunityId,
                    accountId: project.accountId,
                  },
                  project: {
                    existingProject: {
                      name: project.name,
                      defaultBudgetId: project.defaultBudgetId,
                    },
                  },
                  budget: {
                    billingType: budget?.billingType,
                    amount: financeBudget?.amount,
                    hours:
                      budget?.billingType === BillingType.MILESTONE
                        ? budget.structure?.[0]?.hours || financeBudget?.hours
                        : null,
                    startDate: budget?.startDate,
                    softStartDate: budget?.softStartDate,
                    endDate: budget?.endDate,
                    extendedEndDate: budget?.extendedEndDate,
                    expectedSowSigningDate: budget?.expectedSowSigningDate || null,
                  },
                  sow: {
                    sowId: Number(account?.lastUploadedSowNumber || 0) + 1,
                    isDocusignLink: isDocusignLinkValid(urlParams?.docusignLink),
                    docusignLink: urlParams?.docusignLink,
                  },
                  team: {
                    id: 'isNeedCreateTeam' in projectTeam ? null : projectTeam?.id,
                    expectedStructure: projectTeam?.expectedStructure || [{}],
                    isNeedCreateTeam: 'isNeedCreateTeam' in projectTeam,
                  },
                });
              });
          }}
        >
          <ProjectUpdateForm />
        </UpdateForm>
      </UpdatePageLayout>
    </>
  );
};

export default UpdateProjectLayout;
